import React, {Component} from "react";
import {Dropdown} from "primereact/dropdown";
import RemotingService from "../../../services/remoting-service/RemotingService";
import PrimeDateInput from "../../../components/date-input/PrimeDateInput";
import {Wizard} from "../../../components";
import BigCalendar from "../../../components/big-calendar/BigCalendar";
import {momentLocalizer, Views} from "react-big-calendar";
import AppointmentCalendarFormats from "../AppointmentCalendarFormats";
import {getMoment, getNow} from "../../../utils/CalendarDateUtil";
import {FormField} from "../../../components/form";
import DateUtil from "../../../utils/DateUtil";
import SchedulingService from "../../../services/scheduling-service/SchedulingService";
import AppointmentCalendarEventFactory from "../calendarevent/AppointmentCalendarEventFactory";
import ContextMenu from "../../../components/context-menu/ContextMenu";
import ContextMenuItem from "../../../components/context-menu/ContextMenuItem";
import DeleteIcon from "../../../components/action-menu/icons/DeleteIcon";
import dialog from "../../../components/dialog/Dialog";
import {AddRotationBlockModal} from "../../staff/staffprofile/schedule/AddRotationBlockModal";
import "./CopyScheduleWizard.css";
import TimezoneUtil from "../../../utils/TimezoneUtil";
import CalendarEventService from "../CalendarEventService";
import moment from "moment";
import ReduxService from "../../../services/redux-service/ReduxService";
import ValidationUtil from "../../../components/form/validator/ValidationUtil";

const WEEK_DAYS = ['SUNDAY', 'MONDAY', 'TUESDAY', 'WEDNESDAY', 'THURSDAY', 'FRIDAY', 'SATURDAY'];

export default class CopyScheduleWizard extends Component {

    constructor(props) {
        super(props);
        this.state = {
            providers: [],
            events: [],
            provider: null,
            weekStartDate: null,
            weekDays: [],
            selectedView: Views.WEEK,
            timezone: null,
            daySchedules: null,
            allRooms: []
        };
    }

    componentDidMount() {
        RemotingService.getRemoteCall('api/staff/list-base', null, (result) => {
            this.setState({providers: result.items})
        });

        SchedulingService.getSchedules((schedules) => {
            this.setState({schedules});
        });
    }

    addBlock({start, end}) {
        if (DateUtil.getDurationInMinutes(start, end) <= 5) {
            return;
        }

        const dayDate = DateUtil.formatDate(start, "YYYY-MM-DD");
        const dayData = this.state.weekDays.find(d =>
            DateUtil.formatDate(d.date, "YYYY-MM-DD") === dayDate
        );

        if (!dayData || !dayData.room) {
            return;
        }

        dialog({
            title: "Add Block",
            component: <AddRotationBlockModal staffId={this.state.provider.id}
                                              room={dayData.room}
                                              start={start}
                                              end={end}
                                              timezone={this.state.timezone}
                                              events={this.state.events}
                                              onAdd={(event) => {
                                                  const events = [...this.state.events, event];
                                                  this.setState({events});
                                              }}/>
        });
    }

    deleteBlock = (selectedEvent) => {
        const events = this.state.events.filter(e => e !== selectedEvent);
        this.setState({events});
    }

    save() {
        const {provider, weekDays, events, timezone} = this.state;

        const selectedDays = weekDays.filter(dayData => dayData.date && dayData.room);

        if (selectedDays.length === 0) {
            return;
        }

        let completedCount = 0;
        const totalCount = selectedDays.length;

        selectedDays.forEach((dayData) => {
            const dayEvents = events.filter(event => {
                const eventDate = DateUtil.formatDate(event.start, "YYYY-MM-DD");
                const selectedDate = DateUtil.formatDate(dayData.date, "YYYY-MM-DD");
                return eventDate === selectedDate;
            });

            const blocks = dayEvents.map(event => ({
                type: event.type,
                startTime: DateUtil.getTimeStringAtZone(event.start, timezone),
                endTime: DateUtil.getTimeStringAtZone(event.end, timezone)
            }));

            const params = {
                roomId: dayData.room.id,
                date: dayData.date,
                blocks: blocks
            };

            RemotingService.postRemoteCall(`api/staff/${provider.id}/copy-rotation-block`, params, () => {
                completedCount++;
                if (completedCount === totalCount) {
                    this.props.close();
                }
            });
        });
    }

    providerChanged(selectedProvider) {
        const country = selectedProvider.departments[0].clinic.country;
        const timezone = TimezoneUtil.getTimeZoneForCountry(country);

        RemotingService.getRemoteCall(`api/scheduling-secure/daySchedule/${selectedProvider.id}`, null, (daySchedules) => {
            this.setState({
                provider: selectedProvider,
                timezone: timezone,
                daySchedules: daySchedules,
                weekStartDate: null,
                weekDays: []
            });
        });
    }

    weekStartDateChanged(selectedDate) {
        if (!selectedDate || !this.state.provider) {
            this.setState({weekStartDate: null, weekDays: []});
            return;
        }

        const weekDays = [];
        for (let i = 0; i < 7; i++) {
            const date = new Date(selectedDate);
            date.setDate(date.getDate() + i);
            const dayName = WEEK_DAYS[date.getDay()];
            const dayBlocks = this.state.daySchedules?.data?.[dayName] || [];
            const hasTemplate = dayBlocks.length > 0;
            const templateBlock = dayBlocks.find(b => b.roomId);
            const templateRoomId = templateBlock?.roomId || null;

            weekDays.push({
                dayName,
                date,
                room: null,
                availableRooms: [],
                hasTemplate,
                templateRoomId
            });
        }

        this.setState({weekStartDate: selectedDate, weekDays}, () => this.fetchAvailableRoomsForWeek());
    }

    fetchAvailableRoomsForWeek() {
        const {weekDays, weekStartDate} = this.state;
        if (!weekStartDate || weekDays.length === 0) return;

        const endDate = new Date(weekStartDate);
        endDate.setDate(endDate.getDate() + 6);

        const params = {
            startDate: DateUtil.formatDate(weekStartDate, "YYYY-MM-DD"),
            endDate: DateUtil.formatDate(endDate, "YYYY-MM-DD")
        };

        RemotingService.getRemoteCall('api/room/available-rooms', params, (roomAvailabilities) => {
            const roomsByDate = {};
            roomAvailabilities.forEach(ra => {
                const dateKey = ra.date;
                if (!roomsByDate[dateKey]) {
                    roomsByDate[dateKey] = [];
                }
                roomsByDate[dateKey].push(ra.room);
            });

            const updatedWeekDays = this.state.weekDays.map(dayData => {
                const dateKey = DateUtil.formatDate(dayData.date, "YYYY-MM-DD");
                const availableRooms = roomsByDate[dateKey] || [];
                const autoFilledRoom = dayData.templateRoomId && availableRooms.length > 0
                    ? availableRooms.find(r => r.id === dayData.templateRoomId) || null
                    : null;

                return {...dayData, availableRooms, room: autoFilledRoom};
            });

            this.setState({weekDays: updatedWeekDays});
        });
    }

    dayRoomChanged(index, selectedRoom) {
        this.setState(prevState => {
            const weekDays = [...prevState.weekDays];
            weekDays[index] = {...weekDays[index], room: selectedRoom};
            return {weekDays};
        });
    }

    clearDayRoom(index) {
        this.setState(prevState => {
            const weekDays = [...prevState.weekDays];
            weekDays[index] = {...weekDays[index], room: null};
            return {weekDays};
        });
    }

    hasAtLeastOneCompleteDay() {
        return this.state.weekDays.some(dayData => dayData.date && dayData.room);
    }

    onCalendarStep(event) {
        const {provider, weekDays, timezone, daySchedules} = this.state;
        const allEvents = [];

        weekDays.forEach((dayData) => {
            if (!dayData.date || !dayData.room) return;

            const tzDate = moment.tz(timezone)
                .year(dayData.date.getFullYear())
                .month(dayData.date.getMonth())
                .date(dayData.date.getDate());

            const blocks = daySchedules?.data?.[dayData.dayName] || [];
            const dayEvents = blocks.map(block =>
                CalendarEventService.createEvent(
                    [dayData.room],
                    tzDate,
                    {...block, roomId: dayData.room.id},
                    provider,
                    timezone
                )
            );

            allEvents.push(...dayEvents);
        });

        this.setState({events: allEvents}, () => ReduxService.decrementRemotingOperationCount());
    }

    getFirstSelectedDate() {
        const firstDay = this.state.weekDays.find(d => d.date && d.room);
        return firstDay?.date || this.state.weekStartDate || new Date();
    }

    formatDayLabel(dayName) {
        return dayName.charAt(0) + dayName.slice(1).toLowerCase();
    }

    formatDateLabel(date) {
        return DateUtil.formatDate(date, "ddd DD MMM");
    }

    renderDayRow(dayData, index) {
        const hasRoom = !!dayData.room;

        return (
            <div key={index} className={`week-day-row ${!dayData.hasTemplate ? 'no-template' : ''}`}>
                <div className="day-info">
                    <span className="day-name">{this.formatDayLabel(dayData.dayName)}</span>
                    <span className="day-date">{this.formatDateLabel(dayData.date)}</span>
                </div>
                <div className="day-room">
                    <Dropdown
                        optionLabel="info"
                        options={dayData.availableRooms}
                        value={dayData.room}
                        disabled={dayData.availableRooms.length === 0}
                        placeholder={dayData.availableRooms.length === 0 ? "No rooms" : "Select Room"}
                        onChange={event => this.dayRoomChanged(index, event.value)}
                        showClear={hasRoom}
                        appendTo={document.body}
                    />
                </div>
            </div>
        );
    }

    render() {
        this.formFields = [];
        const momentInstance = getMoment(this.state.timezone);

        if (this.state.weekStartDate) {
            const startDayOfWeek = this.state.weekStartDate.getDay(); // 0=Sunday, 1=Monday, etc.
            momentInstance.localeData()._week.dow = startDayOfWeek;
        }

        const localizer = momentLocalizer(momentInstance);
        const firstSelectedDate = this.getFirstSelectedDate();
        const isProviderSelected = !!this.state.provider;
        const isWeekSelected = this.state.weekDays.length > 0;

        return (
            <Wizard contentStyle={{width: 650}}
                    show={true}
                    close={() => this.props.close()}
                    header="Copy Schedule"
                    onSubmit={() => this.save()}>

                <Wizard.Page
                    onNext={(event) => {
                        if (!ValidationUtil.isValid(this.formFields)) {
                            ValidationUtil.stopEventPropagationIfValidationFails(event, this.formFields);
                            return;
                        }
                        if (!this.hasAtLeastOneCompleteDay()) {
                            event.preventDefault();
                            event.stopPropagation();
                            return;
                        }
                        this.onCalendarStep(event);
                    }}
                    onPrevious={() => (this.formFields = [])}>
                    <div className="container copy-schedule-weekly">
                        <div className="row mb-3">
                            <div className="col d-flex flex-column">
                                <span>Provider</span>
                                <FormField ref={f => this.formFields.push(f)}
                                           required validateOn={this.state.provider}>
                                    <Dropdown optionLabel="name"
                                              options={this.state.providers}
                                              value={this.state.provider}
                                              onChange={event => this.providerChanged(event.value)}/>
                                </FormField>
                            </div>
                        </div>

                        <div className="row mb-3">
                            <div className="col d-flex flex-column">
                                <span>Week Starting</span>
                                <FormField ref={f => this.formFields.push(f)}
                                           required validateOn={this.state.weekStartDate}>
                                    <PrimeDateInput
                                        containerStyle={{width: "100%"}}
                                        value={this.state.weekStartDate}
                                        disabled={!isProviderSelected}
                                        onChange={value => this.weekStartDateChanged(value)}
                                    />
                                </FormField>
                            </div>
                        </div>

                        {isWeekSelected && (
                            <div className="week-days-container">
                                {this.state.weekDays.map((dayData, index) =>
                                    this.renderDayRow(dayData, index)
                                )}
                            </div>
                        )}

                        {isProviderSelected && isWeekSelected && !this.hasAtLeastOneCompleteDay() && (
                            <div className="row mt-2">
                                <div className="col">
                                    <small className="text-danger">
                                        Please select at least one room for a day
                                    </small>
                                </div>
                            </div>
                        )}
                    </div>
                </Wizard.Page>

                <Wizard.Page onPrevious={() => this.formFields = []}>
                    <BigCalendar
                        className="copy-schedule-calendar no-current-time-indicator"
                        style={{height: 2000}}
                        dragDrop={true}
                        toolbar={false}
                        defaultView={this.state.selectedView}
                        localizer={localizer}
                        formats={AppointmentCalendarFormats}
                        getNow={() => getNow(new Date(), momentInstance)}
                        step={5}
                        timeslots={12}
                        date={firstSelectedDate}
                        min={momentInstance().hour(7).minute(0).utcOffset('GST' === this.state.timezone ? 4 : 3).toDate()}
                        max={momentInstance().hour(21).minute(0).utcOffset('GST' === this.state.timezone ? 4 : 3).toDate()}
                        events={this.state.events}
                        resizableAccessor={event => false}
                        draggableAccessor={event => false}
                        selectable onSelectSlot={event => this.addBlock(event)}
                        components={{
                            event: AppointmentCalendarEventFactory({
                                timeZoneGetter: () => this.state.timezone,
                                viewGetter: () => this.state.selectedView,
                                refreshCalendarData: () => {},
                                appointmentActionsVisible: false,
                                eventContextMenuVisible: true,
                                showEventContextMenu: (e, event) => this.eventContextMenu.show(e, event)
                            }),
                            timeGutterHeader: () =>
                                <div className="font-size-small">
                                    <div className="font-weight-bold">Weekly Schedule</div>
                                    <div>{this.state.provider?.name}</div>
                                </div>
                        }}
                        resources={[this.state.provider]}
                        resourceAccessor="staffId"
                        resourceIdAccessor="id"
                        resourceTitleAccessor={staff =>
                            <div className="custom-resource-header" id={staff.id}>
                                <div>{staff.specialityName}</div>
                                <div>{staff.name}</div>
                            </div>
                        }
                    />
                    <ContextMenu ref={el => this.eventContextMenu = el}>
                        <ContextMenuItem icon={<DeleteIcon/>}
                                         label="Delete Block"
                                         onClick={(event) => this.deleteBlock(event)}/>
                    </ContextMenu>
                </Wizard.Page>
            </Wizard>
        )
    }

}
